import React from 'react';
import PropTypes from 'prop-types';
import useLanguage from '../../core/language/useLanguage';
import { getTranslation } from '../../core/utils/helpers';
import './legalPolicyLinks.scss';

const LegalPolicyLinks = ({
    className,
    fontSize,
    showTitle,
    layout,
    showSeparator,
}) => {
    const { language } = useLanguage();

    const policies = [
        {
            key: 'terms-of-use',
            url: 'https://www.prakashastrologer.com/terms-of-use/',
            label: getTranslation(language, 'terms_of_use_link_text', 'Terms of Use'),
        },
        {
            key: 'terms-of-services',
            url: 'https://www.prakashastrologer.com/terms-of-services/',
            label: getTranslation(language, 'terms_of_services_link_text', 'Terms of Services'),
        },
        {
            key: 'acceptable-use',
            url: 'https://www.prakashastrologer.com/acceptable-use-policy/',
            label: getTranslation(language, 'acceptable_use_policy_link_text', 'Acceptable Use Policy'),
        },
        {
            key: 'privacy-policy',
            url: 'https://www.prakashastrologer.com/privacy-policy/',
            label: getTranslation(language, 'privacy_policy_link_text', 'Privacy Policy'),
        },
        {
            key: 'disclaimer',
            url: 'https://www.prakashastrologer.com/disclaimer/',
            label: getTranslation(language, 'disclaimer_link_text', 'Disclaimer'),
        },
    ];

    const getContainerClass = () => {
        const baseClass = 'legal-policy-links';
        if (layout === 'vertical') return `${baseClass} ${baseClass}--vertical`;
        if (layout === 'sidebar') return `${baseClass} ${baseClass}--sidebar`;
        if (layout === 'inline') return `${baseClass} ${baseClass}--inline`;
        return `${baseClass} ${baseClass}--grid`;
    };

    const renderLink = (policy) => (
        <a
            key={policy.key}
            href={policy.url}
            target="_blank"
            rel="noreferrer"
            className="legal-policy-links__link"
            style={{ fontSize: fontSize ? `${fontSize}px` : undefined }}>
            {policy.label}
        </a>
    );

    const renderSeparator = () => (
        showSeparator && <span className="legal-policy-links__separator">•</span>
    );

    if (layout === 'inline') {
        return (
            <div className={`${getContainerClass()} ${className || ''}`}>
                {showTitle && (
                    <span
                        className="legal-policy-links__title legal-policy-links__title--inline"
                        style={{ fontSize: fontSize ? `${fontSize}px` : undefined }}>
                        {getTranslation(language, 'legal_policies_agreement_text', 'By proceeding, you agree to our:')}
                    </span>
                )}
                {policies.map((policy, index) => (
                    <React.Fragment key={policy.key}>
                        {renderLink(policy)}
                        {index < policies.length - 1 && renderSeparator()}
                    </React.Fragment>
                ))}
            </div>
        );
    }

    if (layout === 'sidebar') {
        return (
            <div className={`${getContainerClass()} ${className || ''}`}>
                {showTitle && (
                    <h3
                        className="legal-policy-links__title"
                        style={{ fontSize: fontSize ? `${fontSize + 2}px` : undefined }}>
                        {getTranslation(language, 'legal_policies_title', 'Legal Policies')}
                    </h3>
                )}
                <div className="legal-policy-links__container">
                    {policies.map(renderLink)}
                </div>
            </div>
        );
    }

    if (layout === 'vertical') {
        return (
            <div className={`${getContainerClass()} ${className || ''}`}>
                {showTitle && (
                    <div
                        className="legal-policy-links__title"
                        style={{ fontSize: fontSize ? `${fontSize}px` : undefined }}>
                        {getTranslation(language, 'legal_policies_title', 'Legal Policies')}
                    </div>
                )}
                <div className="legal-policy-links__container">
                    <div className="legal-policy-links__row">
                        {renderLink(policies[0])}
                        {renderSeparator()}
                        {renderLink(policies[1])}
                    </div>
                    <div className="legal-policy-links__row">
                        {renderLink(policies[2])}
                        {renderSeparator()}
                        {renderLink(policies[3])}
                    </div>
                    <div className="legal-policy-links__row legal-policy-links__row--center">
                        {renderLink(policies[4])}
                    </div>
                </div>
            </div>
        );
    }

    // Grid layout (default) - exactly like mobile
    return (
        <div className={`${getContainerClass()} ${className || ''}`}>
            {showTitle && (
                <div
                    className="legal-policy-links__title"
                    style={{ fontSize: fontSize ? `${fontSize}px` : undefined }}>
                    {getTranslation(language, 'legal_policies_agreement_text', 'By proceeding, you agree to our:')}
                </div>
            )}
            <div className="legal-policy-links__container">
                <div className="legal-policy-links__row">
                    {renderLink(policies[0])}
                    {renderSeparator()}
                    {renderLink(policies[1])}
                </div>
                <div className="legal-policy-links__row">
                    {renderLink(policies[2])}
                    {renderSeparator()}
                    {renderLink(policies[3])}
                </div>
                <div className="legal-policy-links__row">
                    {renderLink(policies[4])}
                </div>
            </div>
        </div>
    );
};

LegalPolicyLinks.propTypes = {
    className: PropTypes.string,
    textColor: PropTypes.string,
    fontSize: PropTypes.number,
    showTitle: PropTypes.bool,
    titleColor: PropTypes.string,
    layout: PropTypes.oneOf(['grid', 'vertical', 'sidebar', 'inline']),
    compact: PropTypes.bool,
    showSeparator: PropTypes.bool,
};

LegalPolicyLinks.defaultProps = {
    className: '',
    textColor: '#E1BF40',
    fontSize: 12,
    showTitle: true,
    titleColor: '#FFFFFF',
    layout: 'grid',
    compact: false,
    showSeparator: true,
};

export default LegalPolicyLinks;
